import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Users, UserCheck, Settings, 
  LogOut, Menu, X, Sliders, Megaphone, Sparkles, Repeat
} from 'lucide-react';
import NotificationBell from '../Notifications/NotificationBell';

const AdminLayout = ({ children }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: <LayoutDashboard className="h-5 w-5" />, label: 'Tableau de bord', path: '/admin' },
    { icon: <Users className="h-5 w-5" />, label: 'Clients', path: '/admin/clients' },
    { icon: <UserCheck className="h-5 w-5" />, label: 'Vendeurs', path: '/admin/vendors' },
    { icon: <Repeat className="h-5 w-5" />, label: 'Renouvellements', path: '/admin/renewals' },
    { icon: <Megaphone className="h-5 w-5" />, label: 'Influenceurs', path: '/admin/influencers' },
    { icon: <Sparkles className="h-5 w-5" />, label: 'Assistant IA', path: '/admin/assistant' },
    { icon: <Sliders className="h-5 w-5" />, label: 'Paramètres', path: '/admin/settings' },
    { icon: <Settings className="h-5 w-5" />, label: 'Mon Compte', path: '/settings' },
  ];

  const currentPageLabel = menuItems.find(item => item.path === location.pathname)?.label || 'Administration';

  return (
    <div className="flex h-screen bg-[#0A0A0B] overflow-hidden" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#121214] border-b border-[#3A3A3D] z-40 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-[#B8B8BA] hover:text-[#FF6B35] p-1"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div>
            <h1 className="text-base font-bold text-[#FF6B35]">IPTV4KFR</h1>
            <p className="text-[#6B6B6E] text-xs">Admin</p>
          </div>
        </div>
        <NotificationBell />
      </div>

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — navigation only, no bell */}
      <aside 
        className={`
          fixed md:static inset-y-0 left-0 z-50
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${isMobile ? 'w-72' : sidebarOpen ? 'w-64' : 'w-20'}
          bg-[#121214] border-r border-[#3A3A3D] transition-all duration-300 flex flex-col
          ${isMobile ? 'pt-16' : ''}
        `}
      >
        {/* Desktop Sidebar Header */}
        <div className="hidden md:flex p-6 border-b border-[#3A3A3D] items-center justify-between">
          {sidebarOpen && (
            <div>
              <h1 className="text-xl font-bold text-[#FF6B35]">IPTV4KFR</h1>
              <p className="text-[#6B6B6E] text-xs">Administration</p>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-[#B8B8BA] hover:text-[#FF6B35]"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 md:py-6">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => isMobile && setSidebarOpen(false)}
              className={`flex items-center space-x-3 px-4 md:px-6 py-3 transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#FF6B35]/10 border-r-2 border-[#FF6B35] text-[#FF6B35]'
                  : 'text-[#B8B8BA] hover:bg-[#1A1A1D] hover:text-white'
              }`}
              data-testid={`nav-${item.path.split('/').pop()}`}
            >
              {item.icon}
              {(sidebarOpen || isMobile) && <span className="font-medium text-sm">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-4 md:p-6 border-t border-[#3A3A3D]">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 text-[#B8B8BA] hover:text-[#FF3B5C] transition-colors w-full"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-5 w-5" />
            {(sidebarOpen || isMobile) && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 flex flex-col overflow-hidden ${isMobile ? 'pt-14' : ''}`}>
        {/* Desktop Top Bar — notification bell top-right */}
        <div className="hidden md:flex items-center justify-between px-6 py-3 bg-[#121214]/80 backdrop-blur-md border-b border-[#3A3A3D]/50 flex-shrink-0" data-testid="desktop-top-bar">
          <p className="text-[#6B6B6E] text-sm">{currentPageLabel}</p>
          <NotificationBell />
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
